<template>
  <main-template>
    <v-row class="align-center mt-2 mb-2">
      <v-col>
        <div class="d-flex align-center">
          <v-btn color="primary" x-small text @click="goBack">
            <v-icon>fa fa-angle-left</v-icon>
          </v-btn>
          <div class="ml-1">
            <h1 class="mb-0" style="line-height: 1.2">Censos Escolares</h1>
          </div>
        </div>
      </v-col>
      <v-col cols="auto">
        <v-btn color="primary" @click="dialog = true">
          <v-icon left small>fa fa-plus</v-icon>
          Novo Censo
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <resumo-card
        v-for="card in resumo"
        :key="card.label"
        :label="card.label"
        :valor="card.valor"
        :icone="card.icone"
        :cor="card.cor"
        :cor-fundo="card.corFundo"
        :loading="loading"
      />
    </v-row>

    <v-row class="mt-4">
      <v-col>
        <v-data-table
          :headers="headers"
          :items="censos"
          :loading="loading"
          class="rounded-xl"
          outlined
        >
          <template #[`item.finalizado`]="{ item }">
            <v-chip small :color="item.finalizado ? 'green' : 'orange'" dark>
              {{ item.finalizado ? "Finalizado" : "Em Aberto" }}
            </v-chip>
          </template>

          <template #[`item.acoes`]="{ item }">
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="acao-btn"
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click="verCenso(item)"
                >
                  <v-icon small>fa fa-cog</v-icon>
                </v-btn>
              </template>
              <span>Configurar</span>
            </v-tooltip>
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="acao-btn"
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click="editarCenso(item)"
                >
                  <v-icon small>fa fa-edit</v-icon>
                </v-btn>
              </template>
              <span>Editar</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <base-dialog
      v-model="dialog"
      :title="editandoId ? 'Editar Censo' : 'Novo Censo'"
      :loading="salvando"
      @confirm="salvarCenso"
    >
      <v-text-field v-model="form.descricao" label="Descrição" outlined dense />
      <v-select
        v-model="form.ano_id"
        :items="anos"
        item-text="descricao"
        item-value="id"
        label="Ano"
        outlined
        dense
      />
    </base-dialog>
  </main-template>
</template>

<script>
import ResumoCard from "@/components/CensoEscolar/ResumoCard.vue";
import BaseDialog from "@/components/EDialog.vue";

export default {
  components: { ResumoCard, BaseDialog },

  data() {
    return {
      censos: [],
      anos: [],
      loading: true,
      salvando: false,
      dialog: false,
      editandoId: null,
      form: { descricao: "", ano_id: null },
      headers: [
        { text: "Descrição", value: "descricao" },
        { text: "Ano", value: "ano.descricao" },
        { text: "Status", value: "finalizado" },
        { text: "Ações", value: "acoes", sortable: false, align: "center" },
      ],
    };
  },

  computed: {
    resumo() {
      return [
        {
          label: "TOTAL",
          valor: this.censos.length,
          icone: "fa fa-list",
          cor: "primary",
          corFundo: "#e8f0fe",
        },
        {
          label: "EM ABERTO",
          valor: this.censos.filter((c) => !c.finalizado).length,
          icone: "fa fa-clock",
          cor: "orange darken-2",
          corFundo: "#fff3e0",
        },
        {
          label: "FINALIZADOS",
          valor: this.censos.filter((c) => c.finalizado).length,
          icone: "fa fa-check-circle",
          cor: "green darken-1",
          corFundo: "#e8f5e9",
        },
      ];
    },
  },

  methods: {
    goBack() {
      this.$router.go(-1);
    },

    fecharDialog() {
      this.dialog = false;
      this.editandoId = null;
      this.form = { descricao: "", ano_id: null };
    },

    editarCenso(censo) {
      this.editandoId = censo.id;
      this.form = { descricao: censo.descricao, ano_id: censo.ano ? censo.ano.id : censo.ano_id };
      this.dialog = true;
    },

    async salvarCenso() {
      this.salvando = true;
      try {
        if (this.editandoId) {
          await this.$services.censoService.update(this.editandoId, this.form);
        } else {
          await this.$services.censoService.store(this.form);
        }
        this.censos = await this.$services.censoService.index();
        const sucesso = this.editandoId ? "Censo atualizado com sucesso!" : "Censo criado com sucesso!";
        this.fecharDialog();
        this.$toast.success(sucesso);
      } catch (e) {
        this.exibirErroSalvar(e);
      } finally {
        this.salvando = false;
      }
    },

    // Mostra a mensagem real do backend (ex.: descrição duplicada) em vez de texto fixo.
    exibirErroSalvar(e) {
      const erros = e?.response?.data?.errors;
      if (erros) {
        Object.values(erros)
          .flat()
          .forEach((msg) => this.$toast.error(msg));
        return;
      }
      this.$toast.error(e?.response?.data?.message || "Erro ao salvar censo.");
    },

    verCenso(censo) {
      this.$router.push({ name: "censo-escolar-show", params: { id: censo.id } });
    },
  },

  async created() {
    [this.censos, this.anos] = await Promise.all([
      this.$services.censoService.index(),
      this.$services.anosService.syncAll(),
    ]);

    this.loading = false;
  },
};
</script>

<style scoped>
.acao-btn {
  transition: background-color 0.2s ease, transform 0.2s ease;
}
.acao-btn:hover {
  background-color: rgba(25, 118, 210, 0.12);
  transform: scale(1.1);
}
</style>
